/**
 * Shows a claim's printable PDF (generated by the API on the official form) in a new tab.
 *
 * Browsers only allow pop-ups opened directly from a click, so callers open the tab first with
 * openPdfTab() — before any await — and hand it to showClaimPdf() once the PDF is ready.
 */
import billingService from "../../services/billingService";

export const openPdfTab = (): Window | null => {
  const tab = window.open("", "_blank");
  if (tab) {
    tab.document.title = "Preparing claim form…";
    tab.document.body.style.fontFamily = "sans-serif";
    tab.document.body.textContent = "Preparing claim form…";
  }
  return tab;
};

export const showClaimPdf = async (tab: Window | null, claimId: string, claimNumber?: string): Promise<void> => {
  try {
    const pdf = await billingService.getClaimPdf(claimId);
    const url = URL.createObjectURL(new Blob([pdf], { type: "application/pdf" }));
    if (tab && !tab.closed) {
      tab.location.href = url;
    } else {
      // Pop-up blocked: fall back to a download
      const link = document.createElement("a");
      link.href = url;
      link.download = `claim-${claimNumber || claimId}.pdf`;
      link.click();
    }
    // The tab has loaded the document by then; free the memory
    window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (err) {
    tab?.close();
    throw err;
  }
};

/**
 * Direct print using a hidden iframe on the current page (without opening a new browser tab).
 * Matches the print pattern in ResidentsPage and MedicationSchedulePage.
 */
export const printClaimPdfDirect = async (claimId: string): Promise<void> => {
  const existingIframe = document.getElementById("print-iframe-claim");
  if (existingIframe && document.body.contains(existingIframe)) {
    document.body.removeChild(existingIframe);
  }

  const pdf = await billingService.getClaimPdf(claimId);
  const blob = new Blob([pdf], { type: "application/pdf" });
  const url = window.URL.createObjectURL(blob);

  const iframe = document.createElement("iframe");
  iframe.id = "print-iframe-claim";
  iframe.style.visibility = "hidden";
  iframe.style.position = "fixed";
  iframe.style.right = "0";
  iframe.style.bottom = "0";
  iframe.style.width = "1px";
  iframe.style.height = "1px";
  iframe.style.border = "0";
  iframe.src = url;
  document.body.appendChild(iframe);

  return new Promise<void>((resolve, reject) => {
    let finished = false;

    iframe.onload = () => {
      if (!finished) {
        finished = true;
        resolve();
      }
      setTimeout(() => {
        try {
          if (iframe.contentWindow) {
            iframe.contentWindow.focus();
            iframe.contentWindow.print();
          }
        } catch (printErr) {
          console.error("Direct print failed:", printErr);
        }
      }, 500);
    };

    iframe.onerror = (e) => {
      if (!finished) {
        finished = true;
        reject(e);
      }
    };

    // Safety timeout: resolve if onload does not fire within 10 seconds
    setTimeout(() => {
      if (!finished) {
        finished = true;
        resolve();
      }
    }, 10000);

    // Keep iframe and revoke object URL after 5 minutes so printing is not interrupted
    setTimeout(() => {
      window.URL.revokeObjectURL(url);
    }, 300000);
  });
};


/** Saves a PDF the API returned (e.g. a settlement receipt) as a download; works after an await. */
export const downloadPdf = (pdf: Blob, filename: string): void => {
  const url = URL.createObjectURL(new Blob([pdf], { type: "application/pdf" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
};

/**
 * Direct print using a hidden iframe on the current page for an individual invoice.
 */
export const printInvoicePdfDirect = async (invoiceId: string): Promise<void> => {
  const existingIframe = document.getElementById("print-iframe-invoice");
  if (existingIframe && document.body.contains(existingIframe)) {
    document.body.removeChild(existingIframe);
  }

  const pdf = await billingService.getIndividualInvoicePdf(invoiceId);
  const blob = new Blob([pdf], { type: "application/pdf" });
  const url = window.URL.createObjectURL(blob);

  const iframe = document.createElement("iframe");
  iframe.id = "print-iframe-invoice";
  iframe.style.visibility = "hidden";
  iframe.style.position = "fixed";
  iframe.style.right = "0";
  iframe.style.bottom = "0";
  iframe.style.width = "1px";
  iframe.style.height = "1px";
  iframe.style.border = "0";
  iframe.src = url;
  document.body.appendChild(iframe);

  return new Promise<void>((resolve, reject) => {
    let finished = false;

    iframe.onload = () => {
      if (!finished) {
        finished = true;
        resolve();
      }
      setTimeout(() => {
        try {
          if (iframe.contentWindow) {
            iframe.contentWindow.focus();
            iframe.contentWindow.print();
          }
        } catch (printErr) {
          console.error("Direct invoice print failed:", printErr);
        }
      }, 500);
    };

    iframe.onerror = (e) => {
      if (!finished) {
        finished = true;
        reject(e);
      }
    };

    setTimeout(() => {
      if (!finished) {
        finished = true;
        resolve();
      }
    }, 10000);

    setTimeout(() => {
      window.URL.revokeObjectURL(url);
    }, 300000);
  });
};

/** Saves an individual invoice PDF as a download file. */
export const downloadInvoicePdf = async (invoiceId: string, invoiceNumber?: string): Promise<void> => {
  const pdf = await billingService.getIndividualInvoicePdf(invoiceId);
  downloadPdf(pdf, `invoice-${invoiceNumber || invoiceId}.pdf`);
};

/** Opens an individual invoice PDF in a new browser tab. */
export const openInvoicePdf = async (invoiceId: string, invoiceNumber?: string): Promise<void> => {
  const pdf = await billingService.getIndividualInvoicePdf(invoiceId);
  const blob = new Blob([pdf], { type: "application/pdf" });
  const url = window.URL.createObjectURL(blob);
  const newTab = window.open(url, "_blank");
  if (!newTab) {
    downloadPdf(blob, `invoice-${invoiceNumber || invoiceId}.pdf`);
  } else {
    setTimeout(() => window.URL.revokeObjectURL(url), 60000);
  }
};

